import { Link } from 'react-router-dom'

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last updated: April 12, 2026</p>

      <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">1. Information We Collect</h2>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mt-3">Account Information</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            When you create an account, we collect your name, email address, and profile information provided through your authentication provider (e.g., Google, GitHub). We use Clerk for authentication and do not store your passwords directly.
          </p>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mt-3">Content</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We store the stories, branches, comments, ratings, and other content you create on the Platform.
          </p>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mt-3">Usage Data</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We collect information about how you use the Platform, including pages visited, reading progress, and feature interactions. This data helps us improve the user experience.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1">
            <li>To provide and maintain the Platform</li>
            <li>To personalize your experience (e.g., content recommendations)</li>
            <li>To process transactions and manage StoryCoins</li>
            <li>To communicate with you about your account and Platform updates</li>
            <li>To enforce our Terms of Use and community guidelines</li>
            <li>To improve the Platform and develop new features</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">3. Information Sharing</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We do not sell your personal information. We may share information with:
          </p>
          <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1">
            <li><strong>Service providers:</strong> Clerk (authentication), Convex (backend infrastructure), and Stripe (payment processing) who help us operate the Platform</li>
            <li><strong>Other users:</strong> Your public profile, published stories, and branches are visible to other users</li>
            <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">4. Data Storage and Security</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            Your data is stored using Convex's cloud infrastructure. We implement appropriate technical and organizational measures to protect your information. However, no method of storage or transmission is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">5. Your Rights</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            Depending on your jurisdiction, you may have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1">
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your account and associated data</li>
            <li>Export your content and data</li>
            <li>Opt out of non-essential communications</li>
          </ul>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
            To exercise these rights, contact us at <a href="mailto:privacy@storybranch.app" className="text-primary-600 dark:text-primary-400 hover:underline">privacy@storybranch.app</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">6. Cookies and Tracking</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We use essential cookies for authentication and session management. We do not use third-party advertising trackers. Authentication cookies are managed by Clerk and are necessary for the Platform to function.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">7. Children's Privacy</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            The Platform is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us and we will delete it.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">8. Third-Party Services</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            The Platform integrates with third-party services including:
          </p>
          <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1">
            <li><strong>Clerk</strong> — Authentication and user management</li>
            <li><strong>Convex</strong> — Backend database and real-time infrastructure</li>
            <li><strong>Stripe</strong> — Payment processing</li>
            <li><strong>Project Gutenberg</strong> — Public domain book content</li>
          </ul>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
            Each service has its own privacy policy. We encourage you to review them.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">9. Data Retention</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We retain your account information and content for as long as your account is active. If you delete your account, we will remove your personal information within 30 days. Published content that has been branched by others may remain on the Platform in anonymized form.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">10. Changes to This Policy</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of material changes via the Platform or by email. Continued use of the Platform constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">11. Contact Us</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            For privacy-related inquiries, please contact us at <a href="mailto:privacy@storybranch.app" className="text-primary-600 dark:text-primary-400 hover:underline">privacy@storybranch.app</a>.
          </p>
        </section>
      </div>

      <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline text-sm">
          ← Terms of Use
        </Link>
      </div>
    </div>
  )
}
